import { useState } from "react";

import DescriptionDetails from "components/DescriptionDetails";
import FormFieldTimestamp from "components/FormFields/Timestamp";
import FormFieldViewText from "components/FormFields/ViewText";
import ListEntitiesItem from "components/ListEntitiesItem";
import SpaceAccessBadge from "components/SpaceAccessBadge";
import SpacePath from "components/SpacePath";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import LinkButton from "ds/components/Button/LinkButton";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import FeedbackActions from "ds/components/Feedback/Actions";
import FormField from "ds/components/Form/Field";
import { useShowFullDescriptionWithBackArrow } from "ds/components/FullDescriptionDrawer/useShowFullDescriptionWithBackArrow";
import TabWithCounter from "ds/components/Tab/WithCounter";
import TabPanel from "ds/components/TabPanel";
import Typography from "ds/components/Typography";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useTypedContext from "hooks/useTypedContext";
import { ApiKeyType } from "types/generated";
import { UserManagementActivationStatus } from "views/Account/Settings/types";
import { SpacesContext } from "views/Account/SpacesProvider";
import { AnalyticsPagePersonal } from "hooks/useAnalytics/pages/personal";

import { ApiKeyLegacyListView } from "../types";
import { formatApiKeyType } from "../utils";
import styles from "./styles.module.css";

type ApiKeysDetailsDrawerContentProps = {
  apiKey: ApiKeyLegacyListView;
  activationStatus: UserManagementActivationStatus;
  analyticsPage: AnalyticsPageOrganization | AnalyticsPagePersonal;
};

enum Tabs {
  Spaces = "spaces",
  Groups = "groups",
}

const ApiKeysDetailsDrawerContent = ({
  apiKey,
  activationStatus,
  analyticsPage,
}: ApiKeysDetailsDrawerContentProps) => {
  const [currentTab, setCurrentTab] = useState<Tabs>(Tabs.Spaces);
  const { spaces } = useTypedContext(SpacesContext);

  const onOpenFullDescription = useShowFullDescriptionWithBackArrow(apiKey.description);

  return (
    <>
      <DrawerHeader justify="between">
        <DrawerHeaderTitle title="Access details" />
        <DrawerCloseIcon />
      </DrawerHeader>
      <DrawerBody>
        {activationStatus === UserManagementActivationStatus.INACTIVE && (
          <Banner variant="warning" title="Identity Access Management is inactive">
            Access rules will not take effect until you activate Identity Access Management.
            <FeedbackActions>
              <LinkButton
                to="/settings/management-strategy/"
                analyticsPage={analyticsPage}
                analyticsTitle="Callout Strategy Click"
                variant="contrast"
                size="small"
              >
                Choose strategy
              </LinkButton>
            </FeedbackActions>
          </Banner>
        )}
        <FormFieldViewText label="Name" value={apiKey.name} />
        <FormFieldViewText label="Key ID" value={apiKey.id} withCopy />
        <FormField label="Type">
          <Typography tag="span" variant="p-body2">
            {formatApiKeyType(apiKey.type)}
          </Typography>
        </FormField>
        {apiKey.type === ApiKeyType.Oidc && (
          <>
            <FormFieldViewText label="Issuer" value={apiKey.oidcSettings?.issuer} />
            <FormFieldViewText label="Client ID (audience)" value={apiKey.oidcSettings?.clientId} />
            <FormFieldViewText
              label="Subject expression"
              value={apiKey.oidcSettings?.subjectExpression}
            />
          </>
        )}
        {apiKey.creator && <FormFieldViewText label="Creator" value={apiKey.creator} />}
        {apiKey.lastUsedAt && (
          <FormFieldTimestamp label="Last used" timestamp={apiKey.lastUsedAt} />
        )}
        <DescriptionDetails
          description={apiKey.description}
          onOpenFullDescription={onOpenFullDescription}
        />

        <Box
          direction="row"
          gap="medium"
          align="center"
          className={styles.tabs}
          role="tablist"
          aria-label="Access details"
        >
          <TabWithCounter<Tabs>
            onClick={setCurrentTab}
            isActive={currentTab === Tabs.Spaces}
            id={Tabs.Spaces}
            label="Spaces"
            count={apiKey.spaceCount}
          />
          <TabWithCounter<Tabs>
            onClick={setCurrentTab}
            isActive={currentTab === Tabs.Groups}
            id={Tabs.Groups}
            label="Groups"
            count={apiKey.teamCount}
          />
        </Box>

        <TabPanel isActive={currentTab === Tabs.Spaces} id={Tabs.Spaces}>
          <div className={styles.listWrapper}>
            {apiKey.accessRules.map(({ space, spaceAccessLevel }, index) => {
              return (
                <ListEntitiesItem key={index} className={styles.listItem}>
                  <Box align="center" justify="between">
                    <SpacePath
                      spaceId={space}
                      fallbackName={spaces.find(({ id }) => id === space)?.name}
                      className={styles.listItemMeta}
                    />
                    <SpaceAccessBadge accessLevel={spaceAccessLevel} />
                  </Box>
                </ListEntitiesItem>
              );
            })}
          </div>
        </TabPanel>
        <TabPanel isActive={currentTab === Tabs.Groups} id={Tabs.Groups}>
          <div className={styles.listWrapper}>
            {apiKey.teams.map((item, index) => {
              return (
                <ListEntitiesItem key={`${item}-${index}`} className={styles.listItem}>
                  <Typography tag="span" variant="p-body2">
                    {item}
                  </Typography>
                </ListEntitiesItem>
              );
            })}
          </div>
        </TabPanel>
      </DrawerBody>
    </>
  );
};

export default ApiKeysDetailsDrawerContent;
